<script setup>
import { computed, ref, onBeforeMount } from "vue";
import IOrderItem from "@components/IOrderItem/IOrderItem.vue";
import IAcquirings from "@components/IAcquirings/IAcquirings.vue";
import IPromo from "@components/IPromo/IPromo.vue";
import useCartStore from "@vueApp/store/modules/cart";
import VueCountdown from "@chenfengyuan/vue-countdown";
import Popper from "vue3-popper";
import { translator } from "@shop/frontend/web/js/services/TranslatorService";
import IDiscountsList from "@components/IDiscountsList/IDiscountsList.vue";

translator.getLanguages();

const cartStore = useCartStore();
const props = defineProps({
	order: {
		type: Object,
		required: true,
	},
});

const subs = computed(() => props.order.cases.filter(caseItem => caseItem.type === "sub"));
const isSub = computed(() => subs.value.length > 0);

async function appliedPromo(orderData) {
	cartStore.updateOrderPrices(orderData);
	const promoTitle = translator.t('main', 'Promocode');
	const promoValue = `${-orderData.discount} ₽`;
	const discountItem = {
		title: promoTitle,
		value: promoValue,
		type: 'promo',
	}
	addDiscount(discountItem);
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
	cartStore.changeLoyalty(ticket, eventId, props.order.id, categoryId, priceId);
}

function addDiscount(discountItem = {}) {
	discounts.value.push(discountItem);
}

function removeDiscount(type = '') {
	discounts.value = discounts.value.filter(discount => discount.type !== type);
}

function editDiscount(newDiscount) {
	discounts.value.forEach(item => {
		if(item.type === newDiscount.type) {
			item.title = newDiscount.title;
			item.value = newDiscount.value;
		}
	});
}

const isAnyPushkinOrCinema = computed(() =>
	props.order.acquirings.filter(
		acquiring => acquiring.payType === "Pushkin" || acquiring.payType === "Cinema"
	)
);

const orderTimer = ref(props.order.paymentDeadline * 1000 - Date.now());
const showCartItem = ref(orderTimer.value > 0 ? true : false);
const discounts = ref([]);

function transformSingleNumberToTwoDigit(props) {
	const formattedProps = {};

	Object.entries(props).forEach(([key, value]) => {
		formattedProps[key] = value < 10 ? `0${value}` : String(value);
	});

	return formattedProps;
}

function deleteExpiredOrderById(orderId) {
	showCartItem.value = false;
	cartStore.deleteOrderByIdFromStore(orderId);
}

const isShowOrderLines = computed(() => props.order.commission || isAnyPushkinOrCinema.value.length);
</script>

<template>
	<transition name="fade">
		<div v-if="showCartItem" class="order__item">
			<div class="order__header">
				<div class="order__info">
					<div class="order__info-number">
						{{ translator.t('main', 'Order') }} №{{ order.id }}</div>
				</div>
				<vue-countdown
					class="order__timer"
					:time="orderTimer"
					:transform="transformSingleNumberToTwoDigit"
					@end="deleteExpiredOrderById(order.id)"
					v-slot="{ minutes, seconds }"
				>
				{{ translator.t('main', 'Pay within') }}
					<span class="order__timer_red">{{ minutes }}:{{ seconds }}</span>
				</vue-countdown>
			</div>
			<div class="order__event">
				<IOrderItem
					v-for="itemCase in order.cases"
					:key="itemCase.id"
					:item-case="{ ...itemCase, orderId: order.id }"
					:trash-button="true"
					:is-history="false"
				/>
			</div>
			<div class="order__low">
				<IPromo
					v-if="!isSub"
					:order-id="order.id"
					:initial-code="order.promocode"
					class="order__promo"
					@applied="orderData => appliedPromo(orderData)"
				/>
				<div v-show="isShowOrderLines" class="order__lines order__low-item">
					<div v-if="order.sale" class="order__line">
						<div class="order__prop">
							{{ isSub ? translator.t('main', 'Discount for purchasing a subscription') : translator.t('main', 'Discount') }}
							<Popper
								v-if="isSub"
								class="light order__sub-info-tooltip"
								placement="top"
								arrow
								hover
								content="Размер скидки зависит от количества выбранных Вами мероприятий."
							>
								<svg width="16" height="16" fill="none">
									<use xlink:href="#question"></use>
								</svg>
							</Popper>
						</div>
						<div class="order__value">{{ order.sale }} ₽</div>
					</div>

					<div v-if="order.commission" class="order__line">
						<div class="order__prop">
							{{ translator.t('main', 'Comission') }}
						</div>
						<div class="order__value">{{ order.commission }} ₽</div>
					</div>

					<div class="order__line" v-if="isAnyPushkinOrCinema.length">
						<div class="order__prop">
							{{ translator.t('main', 'To pay') }}
						</div>
						<div class="order__value" style="font-size: 20px;">
							{{ order.summaryPrice }} ₽
						</div>
					</div>
				</div>
				<i-acquirings :order="order" />
			</div>
		</div>
	</transition>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";

:deep(.popper) {
	width: 280px;
	padding: 5px;

	border-radius: 6px;
	box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
	background-color: #fff;

	color: #000;
	text-align: center;
	font-size: 14px;
	font-weight: 400;
}

:deep(.popper #arrow::before) {
	transform: translateX(-5px) rotate(45deg);
	background: #fff;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.tickets-item {
	margin-right: 16px;
	margin-bottom: 8px;
	display: flex;
	flex-direction: column;
}
@media (max-width: $screen-small) {
	.tickets-item {
		margin-right: 0px;
		width: 100%;
	}
}
</style>
